import { useIsAuthenticated } from '@creditsights/navbar-web';
import { Popup } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import styles from './Base.module.scss';
import ChatBody from './components/ChatBody';
import Footer from './components/Footer';
import Header, { Page, ViewType } from './components/Header';
import History from './components/History';
import { SessionContext } from './context/SessionContext';
import { ToastProvider } from './context/ToastContext';
import { UserContext } from './context/UserContext';
import { useSessionId } from './hooks/chat/useSessionId';
import { useChild } from './hooks/standalone/useChild';
import { useParent } from './hooks/standalone/useParent';
import { useTrackUsage } from './hooks/usage/track';

const cx = classNames.bind(styles);

const defaultWidth = 440;
const defaultHeight = 768;

const highlightedStorageKey = 'cs-genie-highlighted';

const calcHeight = () => Math.min(window.innerHeight - 60, defaultHeight);
const calcWidth = (width: number) => Math.min(window.innerWidth - 16, width);

interface BaseProps {
  standalone?: boolean;
  standaloneUrl?: string;
}

const Base = ({ standalone, standaloneUrl }: BaseProps): React.ReactElement | null => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useIsAuthenticated(queryClient);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [view, setView] = useState<ViewType>('default');
  const [width, setWidth] = useState(defaultWidth);
  const [height, setHeight] = useState(defaultHeight);
  const { acceptedTerms, sessionInfo, createSession, selectSession, isLoading } = useContext(SessionContext);
  const { userId, theme, setTheme } = useContext(UserContext);
  const [page, setPage] = useState<Page>('chat');
  const trackUsage = useTrackUsage();
  const [shouldBeHighlighted, setShouldBeHighlighted] = useState(localStorage.getItem(highlightedStorageKey) !== '1');
  const { data: createdSessionId, refetch } = useSessionId(
    isAuthenticated && isPopupOpen && acceptedTerms && !standalone && !sessionInfo.sessionId
  );
  const { openChild, focusChild, childIsOpened } = useParent({
    childHeight: defaultHeight,
    childWidth: defaultWidth,
    standalone,
    standaloneUrl,
  });
  const { allowed } = useChild({ standalone, createSession: refetch });

  useEffect(() => {
    setPopupOpen(!!standalone);
  }, [standalone]);

  useEffect(() => {
    if (createdSessionId) {
      createSession(createdSessionId);
    }
  }, [createdSessionId, createSession]);

  useEffect(() => {
    const handler = () => {
      setHeight(standalone ? -1 : calcHeight());
      setWidth(standalone ? -1 : calcWidth(view === 'default' ? defaultWidth : defaultWidth * 2));
    };

    handler();
    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, [view, standalone]);

  const handleRemoveHighlight = useCallback(() => {
    localStorage.setItem(highlightedStorageKey, '1');
    setShouldBeHighlighted(false);
  }, [setShouldBeHighlighted]);

  const handleTogglePopup = useCallback(() => {
    handleRemoveHighlight();

    if (childIsOpened) {
      focusChild();
    } else {
      setPopupOpen(!isPopupOpen);
    }
  }, [isPopupOpen, childIsOpened, focusChild, handleRemoveHighlight]);

  const handleClosePopup = useCallback(() => {
    setPopupOpen(false);
  }, []);

  const handleOpenChild = useCallback(() => {
    trackUsage.popoutChat({ sessionId: sessionInfo.sessionId });
    openChild();
    setPopupOpen(false);
  }, [openChild, trackUsage, sessionInfo]);

  const handleChangePage = useCallback(
    (page: Page) => {
      if (page === 'history') {
        trackUsage.openHistory({ sessionId: sessionInfo.sessionId });
      } else if (page === 'chat') {
        trackUsage.returnToChat({ sessionId: sessionInfo.sessionId });
      }

      setPage(page);
    },
    [trackUsage, sessionInfo]
  );

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      trackUsage.clickOnHistoryItem({ sessionId: sessionInfo.sessionId, historySessionId: sessionId });
      selectSession(sessionId);
      setPage('chat');
    },
    [selectSession, trackUsage, sessionInfo]
  );

  const handleCreateNewChat = useCallback(() => {
    if (!isLoading) {
      refetch();
    }
  }, [refetch, isLoading]);

  const handleChangeView = useCallback(
    (view: ViewType) => {
      if (view === 'expanded') {
        trackUsage.expandChatScreen({ sessionId: sessionInfo.sessionId });
      } else {
        trackUsage.minimizeChatScreen({ sessionId: sessionInfo.sessionId });
      }

      setView(view);
    },
    [setView, trackUsage, sessionInfo]
  );

  if (!isAuthenticated || !userId || !allowed) {
    return null;
  }

  const content = (
    <ToastProvider>
      <div
        className={cx('body')}
        style={{ height: height === -1 ? '100%' : height, width: width === -1 ? '100%' : width }}
      >
        <Header
          onClose={handleClosePopup}
          onChangeView={handleChangeView}
          onChangePage={handleChangePage}
          onCreateNewChat={handleCreateNewChat}
          onChangeTheme={setTheme}
          onOpenStandalone={standaloneUrl ? handleOpenChild : undefined}
          theme={theme}
          view={view}
          page={page}
          standalone={standalone}
        />
        {page === 'history' ? <History onSelectSession={handleSelectSession} /> : null}
        {page === 'chat' ? (
          <>
            <ChatBody />
            <Footer />
          </>
        ) : null}
      </div>
    </ToastProvider>
  );

  if (standalone) {
    return <div className={cx('popup', { popupThemeDark: theme === 'dark', standalone })}>{content}</div>;
  }

  return (
    <Popup
      positionFixed
      open={isPopupOpen}
      className={cx('popup', { popupThemeDark: theme === 'dark' })}
      on="click"
      trigger={
        <div
          className={cx('buttonWrapper', {
            buttonWrapperHighlighted: shouldBeHighlighted,
          })}
          onClick={handleTogglePopup}
        >
          <span className={cx('button')} />
          <span>Genie AI</span>
        </div>
      }
      content={content}
      basic
      position="bottom center"
    />
  );
};

export default Base;
